
import { supabase } from '@/integrations/supabase/client';
import { debugSupabaseRequest } from './supabase-helpers';
import { withTimeout } from './asyncTimeout';

/**
 * Determines if a user has a client profile
 */
export async function hasClientProfile(userId: string): Promise<boolean> {
  try {
    console.log('Checking client profile for user:', userId);
    
    const { data, error } = await withTimeout(
      supabase
        .from('clients')
        .select('id')
        .eq('id', userId)
        .maybeSingle(),
      10000
    );

    if (error) {
      console.error('Error checking client profile:', error);
      debugSupabaseRequest('clients', 'select', error);
      if (error.message?.includes('abort') || error.message?.includes('AbortError') || error.code === '20') {
        throw new Error(`Network error checking client profile: ${error.message}`);
      }
      return false;
    }

    const result = !!data;
    console.log('Client profile check result:', result);
    return result;
  } catch (err: any) {
    console.error('Unexpected error checking client profile:', err);
    if (err?.message?.includes('abort') || err?.message?.includes('Network') || err?.message?.includes('timed out') || err?.name === 'AbortError') {
      throw err;
    }
    return false;
  }
}

/**
 * Determines if a user has a practitioner profile
 */
export async function hasPractitionerProfile(userId: string): Promise<boolean> {
  try {
    console.log('Checking practitioner profile for user:', userId);
    
    const { data, error } = await withTimeout(
      supabase
        .from('practitioners')
        .select('id')
        .eq('id', userId)
        .maybeSingle(),
      10000
    );

    if (error) {
      console.error('Error checking practitioner profile:', error);
      debugSupabaseRequest('practitioners', 'select', error);
      if (error.message?.includes('abort') || error.message?.includes('AbortError') || error.code === '20') {
        throw new Error(`Network error checking practitioner profile: ${error.message}`);
      }
      return false;
    }

    const result = !!data;
    console.log('Practitioner profile check result:', result);
    return result;
  } catch (err: any) {
    console.error('Unexpected error checking practitioner profile:', err);
    if (err?.message?.includes('abort') || err?.message?.includes('Network') || err?.message?.includes('timed out') || err?.name === 'AbortError') {
      throw err;
    }
    return false;
  }
}

/**
 * Determines if user is an admin
 */
export async function isAdmin(userId: string): Promise<boolean> {
  try {
    console.log('Checking admin status for user:', userId);
    
    const { data, error } = await withTimeout(
      supabase
        .from('admins')
        .select('user_id')
        .eq('user_id', userId)
        .maybeSingle(),
      10000
    );

    if (error) {
      console.error('Error checking admin status:', error);
      debugSupabaseRequest('admins', 'select', error);
      if (error.message?.includes('abort') || error.message?.includes('AbortError') || error.code === '20') {
        throw new Error(`Network error checking admin status: ${error.message}`);
      }
      return false;
    }

    const result = !!data;
    console.log('Admin status check result:', result);
    return result;
  } catch (err: any) {
    console.error('Unexpected error checking admin status:', err);
    if (err?.message?.includes('abort') || err?.message?.includes('Network') || err?.message?.includes('timed out') || err?.name === 'AbortError') {
      throw err;
    }
    return false;
  }
}

/**
 * Determines if a user has a space host profile
 */
export async function hasHostProfile(userId: string): Promise<boolean> {
  try {
    console.log('Checking host profile for user:', userId);
    
    const { data, error } = await withTimeout(
      supabase
        .from('space_hosts')
        .select('id')
        .eq('id', userId)
        .maybeSingle(),
      10000
    );

    if (error) {
      console.error('Error checking host profile:', error);
      if (error.message?.includes('abort') || error.message?.includes('AbortError') || error.code === '20') {
        throw new Error(`Network error checking host profile: ${error.message}`);
      }
      return false;
    }

    const result = !!data;
    console.log('Host profile check result:', result);
    return result;
  } catch (err: any) {
    console.error('Unexpected error checking host profile:', err);
    if (err?.message?.includes('abort') || err?.message?.includes('Network') || err?.message?.includes('timed out') || err?.name === 'AbortError') {
      throw err;
    }
    return false;
  }
}

/**
 * Determines if user is a client or practitioner or admin or none
 * Uses a single RPC call instead of 4 separate table probes.
 */
export async function determineUserType(userId: string, preferredRole?: 'client' | 'practitioner' | 'admin' | 'host'): Promise<'client' | 'practitioner' | 'admin' | 'host' | 'unknown'> {
  console.log('Determining user type for:', userId, 'preferred role:', preferredRole);

  try {
    const { data, error } = await withTimeout(
      supabase.rpc('get_user_role', { p_user_id: userId }),
      6000
    );

    if (error) {
      console.error('Error calling get_user_role RPC:', error);
      throw error;
    }

    const role = data as string;
    console.log('get_user_role result:', role);

    if (role === 'unknown') {
      return 'unknown';
    }

    // If there's a preferred role, validate it matches
    if (preferredRole) {
      if (role === preferredRole) {
        console.log('User verified for preferred role:', preferredRole);
        return preferredRole;
      }
      console.log('User does NOT have preferred role:', preferredRole, '- actual role:', role);
      return 'unknown';
    }

    return role as 'client' | 'practitioner' | 'admin' | 'host';
  } catch (err: any) {
    console.error('determineUserType RPC failed, falling back to parallel checks:', err);

    // Fallback: parallel table probes (original logic)
    if (preferredRole) {
      let hasRole = false;
      switch (preferredRole) {
        case 'admin': hasRole = await isAdmin(userId); break;
        case 'practitioner': hasRole = await hasPractitionerProfile(userId); break;
        case 'client': hasRole = await hasClientProfile(userId); break;
        case 'host': hasRole = await hasHostProfile(userId); break;
      }
      return hasRole ? preferredRole : 'unknown';
    }

    const [clientResult, practitionerResult, adminResult, hostResult] = await Promise.allSettled([
      hasClientProfile(userId),
      hasPractitionerProfile(userId),
      isAdmin(userId),
      hasHostProfile(userId),
    ]);

    if (adminResult.status === 'fulfilled' && adminResult.value) return 'admin';
    if (practitionerResult.status === 'fulfilled' && practitionerResult.value) return 'practitioner';
    if (hostResult.status === 'fulfilled' && hostResult.value) return 'host';
    if (clientResult.status === 'fulfilled' && clientResult.value) return 'client';

    console.log('User type is unknown - no profile found');
    return 'unknown';
  }
}
